import { Countries, PhoneNumber, TimeZones } from "@core/types";
import { fromPairs, groupBy, toPairs } from "lodash/fp";

export const STATES: string[] = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

export const MONTH_ABBREVIATIONS: { [key: string]: string } = {
  January: "Jan",
  February: "Feb",
  March: "Mar",
  April: "Apr",
  May: "May",
  June: "Jun",
  July: "Jul",
  August: "Aug",
  September: "Sep",
  October: "Oct",
  November: "Nov",
  December: "Dec",
};

export const SUPPORTED_TIMEZONES: {
  tzCode: TimeZones;
  label: string;
  country: Countries;
}[] = [
  // United States
  {
    tzCode: TimeZones.PACIFIC_HONOLULU,
    label: "(GMT-10:00) Hawaii Time",
    country: Countries.UNITED_STATES,
  },
  {
    tzCode: TimeZones.AMERICA_ANCHORAGE,
    label: "(GMT-9:00) Alaska Time",
    country: Countries.UNITED_STATES,
  },
  {
    tzCode: TimeZones.AMERICA_LOS_ANGELES,
    label: "(GMT-8:00) Pacific Time",
    country: Countries.UNITED_STATES,
  },
  {
    tzCode: TimeZones.AMERICA_DENVER,
    label: "(GMT-7:00) Mountain Time",
    country: Countries.UNITED_STATES,
  },
  {
    tzCode: TimeZones.AMERICA_PHOENIX,
    label: "(GMT-7:00) Arizona Time",
    country: Countries.UNITED_STATES,
  },
  {
    tzCode: TimeZones.AMERICA_CHICAGO,
    label: "(GMT-6:00) Central Time",
    country: Countries.UNITED_STATES,
  },
  {
    tzCode: TimeZones.AMERICA_NEW_YORK,
    label: "(GMT-5:00) Eastern Time",
    country: Countries.UNITED_STATES,
  },

  // Australia
  {
    tzCode: TimeZones.PACIFIC_NORFOLK,
    label: "(GMT+11:00) Norfolk",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.ANTARCTICA_MACQUARIE,
    label: "(GMT+11:00) Eastern Time - Macquarie",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_LORD_HOWE,
    label: "(GMT+10:30) Eastern TIme - Lord Howe",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_BRISBANE,
    label: "(GMT+10:00) Eastern Time - Brisbane",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_HOBART,
    label: "(GMT+10:00) Eastern Time - Hobart",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_CURRIE,
    label: "(GMT+10:00) Eastern Time - Currie",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_SYDNEY,
    label: "(GMT+10:00) Eastern Time - Sydney",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_MELBOURNE,
    label: "(GMT+10:00) Eastern Time - Melbourne",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_LINDEMAN,
    label: "(GMT+10:00) Eastern Time - Lindeman",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_ADELAIDE,
    label: "(GMT+9:30) Central Time - Adelaide",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_BROKEN_HILL,
    label: "(GMT+9:30) Central Time - Broken Hill",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_DARWIN,
    label: "(GMT+9:30) Central Time - Darwin",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_EUCLA,
    label: "(GMT+8:45) Western Time - Eucla",
    country: Countries.AUSTRALIA,
  },
  {
    tzCode: TimeZones.AUSTRALIA_PERTH,
    label: "(GMT+8:00) Western Time - Perth",
    country: Countries.AUSTRALIA,
  },

  // United Kingdom
  {
    tzCode: TimeZones.EUROPE_LONDON,
    label: "(GMT+0:00) London",
    country: Countries.UNITED_KINGDOM,
  },

  // Canada
  {
    tzCode: TimeZones.AMERICA_TORONTO,
    label: "(GMT-4:00) Toronto",
    country: Countries.CANADA,
  },
];

export const SUPPORTED_TIMEZONES_GROUPED_BY_COUNTRY = groupBy(
  "country",
  SUPPORTED_TIMEZONES
);

export const SUPPORTED_TIMEZONES_IN_UNITED_STATES =
  SUPPORTED_TIMEZONES_GROUPED_BY_COUNTRY[Countries.UNITED_STATES];
export const SUPPORTED_TIMEZONES_IN_UNITED_KINGDOM =
  SUPPORTED_TIMEZONES_GROUPED_BY_COUNTRY[Countries.UNITED_KINGDOM];
export const SUPPORTED_TIMEZONES_IN_AUSTRALIA =
  SUPPORTED_TIMEZONES_GROUPED_BY_COUNTRY[Countries.AUSTRALIA];
export const SUPPORTED_TIMEZONES_IN_CANADA =
  SUPPORTED_TIMEZONES_GROUPED_BY_COUNTRY[Countries.CANADA];

export const NOCD_PHONE_NUMBERS: { [key in Countries]: PhoneNumber } = {
  [Countries.AUSTRALIA]: {
    raw: "+61240620000",
    formatted: "+61 240 620 000",
  },
  [Countries.UNITED_STATES]: {
    raw: "+13127666780",
    formatted: "+1 (312) 766-6780",
  },
  [Countries.UNITED_KINGDOM]: {
    raw: "+441495708766",
    formatted: "+44 1495 708766",
  },
  [Countries.CANADA]: {
    raw: "+17059998536",
    formatted: "+1 705-999-8536",
  },
  [Countries.OTHER]: {
    raw: "+13127666780",
    formatted: "+1 (312) 766-6780",
  },
};

export const NOCD_BILLING_EMAIL = "billing@nocdhelp.com";
export const NOCD_CARE_TEAM_EMAIL = "care@nocdhelp.com";

export const DAY_ABBREVIATIONS: string[] = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];

export const SUPPORTED_COUNTRIES: (Countries | "Other")[] = [
  Countries.UNITED_STATES,
  Countries.UNITED_KINGDOM,
  Countries.AUSTRALIA,
  Countries.CANADA,
  "Other",
];

export const COUNTRY_CODES: { [key: string]: string } = {
  [Countries.AUSTRALIA]: "AU",
  [Countries.UNITED_KINGDOM]: "UK",
  [Countries.UNITED_STATES]: "US",
  [Countries.CANADA]: "CA",
  [Countries.OTHER]: "OT",
};

export const CANADIAN_PROVINCES = [
  "Alberta",
  "British Columbia",
  "Manitoba",
  "New Brunswick",
  "Newfoundland and Labrador",
  "Northwest Territories",
  "Nova Scotia",
  "Nunavut",
  "Ontario",
  "Prince Edward Island",
  "Quebec",
  "Saskatchewan",
  "Yukon",
];

export const COUNTRIES_BY_COUNTRY_CODE = fromPairs(
  toPairs(COUNTRY_CODES).map(([countryName, countryCode]) => [
    countryCode,
    countryName,
  ])
);

export const FILE_UPLOAD_TIMEOUT_MS = 120000;
